import { Link, useRouterState } from '@tanstack/react-router'
import { ChevronDownIcon, type LucideIcon } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import styles from './SidebarGroup.module.css'
import { useIsSidebarCollapsed } from './useSidebarCollapsed'

export type NavLink = { to: string; label: string; icon?: LucideIcon }

export type NavGroup = { key: string; title: string; links: NavLink[] }

export type SidebarGroupProps = {
  group: NavGroup
  open: boolean
  onOpenChange: (open: boolean) => void
  onNavigate?: () => void
}

function Glyph({ link, rail }: { link: NavLink; rail: boolean }) {
  const Icon = link.icon

  if (Icon) return <Icon className={styles.icon} aria-hidden="true" />
  if (rail)
    return (
      <span className={styles.letter} aria-hidden="true">
        {link.label.charAt(0)}
      </span>
    )

  return null
}

type NavItemProps = { link: NavLink; rail: boolean; onNavigate?: () => void }

function NavItem({ link, rail, onNavigate }: NavItemProps) {
  const anchor = {
    to: link.to,
    className: styles.link,
    activeProps: { className: `${styles.link} ${styles.linkCurrent}` },
    onClick: onNavigate,
  }
  const body = (
    <>
      <Glyph link={link} rail={rail} />
      <span className={rail ? styles.hiddenLabel : undefined}>{link.label}</span>
    </>
  )

  if (!rail) return <Link {...anchor}>{body}</Link>

  return (
    <Tooltip>
      <TooltipTrigger render={<Link {...anchor} />}>{body}</TooltipTrigger>
      <TooltipContent side="right">{link.label}</TooltipContent>
    </Tooltip>
  )
}

export function SidebarGroup({ group, open, onOpenChange, onNavigate }: SidebarGroupProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const rail = useIsSidebarCollapsed()
  const holdsCurrent = group.links.some((link) => pathname.startsWith(link.to))

  if (rail) {
    return (
      <nav aria-label={group.title} className={styles.group} data-collapsed="">
        <div className={styles.links}>
          {group.links.map((link) => (
            <NavItem key={link.to} link={link} rail onNavigate={onNavigate} />
          ))}
        </div>
      </nav>
    )
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={onOpenChange}
      className={styles.group}
      render={<nav aria-label={group.title} />}
    >
      <CollapsibleTrigger
        className={styles.trigger}
        data-current={!open && holdsCurrent ? '' : undefined}
      >
        {group.title}
        <ChevronDownIcon className={styles.chevron} aria-hidden="true" />
      </CollapsibleTrigger>
      <CollapsibleContent className={styles.panel}>
        <div className={styles.links}>
          {group.links.map((link) => (
            <NavItem key={link.to} link={link} rail={false} onNavigate={onNavigate} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
